import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BugReportIcon from '@mui/icons-material/BugReport';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GetAppIcon from '@mui/icons-material/GetApp';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import {
  getSecurityEndpointDetailsHref,
  getSecurityMalwareDetailHref,
  getSecurityNavItemPath,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type { Column, TableData } from 'src/types/table';
import type { WazuhMalwareDetectionItem, WazuhMalwareEndpointItem } from 'src/types/mindtime/wazuhMalware';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrGlobalScopeFilters,
  EdrKpiCard,
  EdrMalwareCharts,
  EdrPageHeader,
  EdrSeverityPill,
  EdrStatusPill,
  edrMutedStyle,
  formatEndpointLabel,
  edrEndpointNameLinkStyle,
  SubscriptionUnavailableMessage,
  type EdrToolbarFilter,
  formatEdrNumber,
}  from '../shared/edrPrimitives';
import {
  securityDashboardFlexGrowSx,
  securityDashboardFlexRowSx,
} from '../shared/securityDashboardLayout';
import { useSecurityDashboardGrid } from '../shared/useSecurityDashboardGrid';
import {
  getMalwareDetectionId,
  getMalwareDetectionListColumns,
  MALWARE_SEVERITY_FILTER_OPTIONS,
  malwareSeverityToLevel,
  toMalwareTitle,
} from './malwareDetailModel';
import { useMalwareData, useMalwareExport } from './useMalwareData';

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Malware Detection', to: getSecurityNavItemPath('malware-detection') ?? '' },
  ],
};


const MalwareDetection: FC = () => {
  const navigate = useNavigate();
  const { setDateRange, setSelectedEndpointId, selectedClientId } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.malwareDetection);
  const {
    severityBand = '',
    malwareType = '',
  } = filters;
  const organizationId = selectedClientId ?? '';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { canWidget, permissionsResolved } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'malware-detection');
  const canSummary = canWidget('summary');
  const canCategories = canWidget('categoriesChart');
  const canList = canWidget('list');
  const canInfectedEndpoints = canWidget('infectedEndpoints');
  const showUnavailable =
    permissionsResolved && !canSummary && !canCategories && !canList && !canInfectedEndpoints;
  const { isMdDown } = useSecurityDashboardGrid();
  const showSummaryChartsRow = canSummary || canCategories;

  const malwareFilters = useMemo(
    () => ({
      severityBand: filters.severityBand,
      malwareType: filters.malwareType,
      organizationId,
    }),
    [filters.severityBand, filters.malwareType, organizationId]
  );

  const openMalwareDetail = useCallback(
    (item: WazuhMalwareDetectionItem) => {
      const id = getMalwareDetectionId(item);
      if (!id) return;
      navigate(getSecurityMalwareDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  const { loading, summary, items: paged, endpoints: endpointsPaged, categories, facets } = useMalwareData(
    page,
    rowsPerPage,
    search,
    malwareFilters
  );
  const { exporting, handleExport } = useMalwareExport(search, malwareFilters);

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;
  const infectedEndpoints = endpointsPaged?.items ?? [];
  const infectedEndpointsTotal = endpointsPaged?.totalRecords ?? 0;

  const handleResetFilters = useSecurityEdrToolbarReset(
    resetFilters,
    setDateRange,
    setPage,
    setSelectedEndpointId
  );

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: MALWARE_SEVERITY_FILTER_OPTIONS,
        value: severityBand,
        multiple: true,
        onChange: (value) => {
          setFilter('severityBand', value);
          setPage(0);
        },
      },
      {
        id: 'type',
        label: 'Malware Type',
        options: facets.types.map((name) => ({ label: name, value: name })),
        value: malwareType,
        multiple: true,
        onChange: (value) => {
          setFilter('malwareType', value);
          setPage(0);
        },
      },
    ],
    [
      severityBand,
      malwareType,
      facets.types,
      setFilter,
    ]
  );

  const columns: Column[] = useMemo(() => getMalwareDetectionListColumns('global'), []);

  const infectedEndpointColumns: Column[] = useMemo(
    () => [
      {
        id: 'endpoint',
        label: 'Endpoint',
        render: (row) => {
          const item = row as unknown as WazuhMalwareEndpointItem;
          const canOpen = Boolean(item.systemWazuhAgentId?.trim());
          return (
            <span style={edrEndpointNameLinkStyle(canOpen)}>
              {item.endpoint ?? item.name ?? '—'}
            </span>
          );
        },
      },
      {
        id: 'hostName',
        label: 'Endpoint Label',
        render: (row) => (
          <span style={edrMutedStyle}>
            {formatEndpointLabel((row as unknown as WazuhMalwareEndpointItem).hostName)}
          </span>
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        render: (row) => {
          const item = row as unknown as WazuhMalwareEndpointItem;
          return (
            <span style={edrMutedStyle}>
              {item.organization ?? item.organizationName ?? '—'}
            </span>
          );
        },
      },
      {
        id: 'detections',
        label: 'Detections',
        align: 'center',
        render: (row) => {
          const item = row as unknown as WazuhMalwareEndpointItem;
          return (
            <span style={{ fontWeight: 700, color: EDR_COLORS.textPrimary }}>
              {formatEdrNumber(item.detections)}
            </span>
          );
        },
      },
      {
        id: 'activeThreats',
        label: 'Active Threats',
        align: 'center',
        render: (row) => {
          const activeThreats = (row as unknown as WazuhMalwareEndpointItem).activeThreats ?? 0;
          if (activeThreats > 0) {
            return <EdrStatusPill tone="critical" label={String(activeThreats)} />;
          }
          return <span style={{ color: EDR_COLORS.textMuted }}>0</span>;
        },
      },
      {
        id: 'highestSeverity',
        label: 'Highest Severity',
        align: 'center',
        render: (row) => {
          const item = row as unknown as WazuhMalwareEndpointItem;
          const severity = item.highestSeverity ?? item.severity;
          if (!severity?.trim()) return <span style={edrMutedStyle}>—</span>;
          return (
            <EdrSeverityPill
              level={malwareSeverityToLevel(severity)}
              label={toMalwareTitle(severity) || '—'}
            />
          );
        },
      },
      {
        id: 'lastDetected',
        label: 'Last Detected',
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhMalwareEndpointItem).lastDetected} />
        ),
      },
    ],
    []
  );

  const infectedEndpointsToolbar = (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Typography
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: EDR_COLORS.textPrimary,
        }}
      >
        Infected Endpoints
      </Typography>
      <Typography style={{ fontSize: 12, color: EDR_COLORS.textMuted }}>
        {formatEdrNumber(infectedEndpointsTotal)} endpoints affected
      </Typography>
    </Box>
  );

  const totalDetections = summary?.totalDetections;
  const criticalThreats = summary?.criticalThreats;
  const detectionsToday = summary?.detectionsToday;

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Malware Detection')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Malware Detection"
          subtitle="Monitor, investigate, and respond to malware detections across all protected endpoints."
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 12 }}
        >
          <EdrGlobalScopeFilters
            pageOrganizationId={organizationId || undefined}
            onChange={() => setPage(0)}
          />
          {canList ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          ) : null}
        </Box>
        {showUnavailable ? (
          <SubscriptionUnavailableMessage messageKey="unavailable.malwareDetection" />
        ) : null}
        {/* Scan Endpoint - no backend API yet */}

        {showSummaryChartsRow ? (
        <Box sx={securityDashboardFlexRowSx(isMdDown)}>
          {canSummary ? (
          <Box
            sx={{
              ...securityDashboardFlexGrowSx,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minHeight: 0,
              alignSelf: 'stretch',
            }}
          >
            <EdrKpiCard
              label="Total Detections"
              value={formatEdrNumber(totalDetections)}
              icon={<BugReportIcon style={{ fontSize: 18 }} />}
              iconColor={EDR_COLORS.critical}
              accent={EDR_COLORS.critical}
            >
              {typeof criticalThreats === 'number' ? (
                <EdrStatusPill
                  tone="critical"
                  label={`${formatEdrNumber(criticalThreats)} Critical`}
                  dot={false}
                />
              ) : (
                <EdrStatusPill tone="neutral" label="Across all monitored endpoints" dot={false} />
              )}
            </EdrKpiCard>
            <EdrKpiCard
              label="Detections Today"
              value={formatEdrNumber(detectionsToday)}
              icon={<CalendarTodayIcon style={{ fontSize: 18 }} />}
              iconColor={EDR_COLORS.high}
              accent={EDR_COLORS.high}
            >
              <EdrStatusPill tone="high" label="New detections in the last 24 hours" dot={false} />
            </EdrKpiCard>
          </Box>
          ) : null}

          {canCategories ? (
          <Box sx={{ ...securityDashboardFlexGrowSx, display: 'flex', alignSelf: 'stretch' }}>
          <EdrMalwareCharts categories={categories?.categories ?? null} />
          </Box>
          ) : null}
        </Box>
        ) : null}

        {canInfectedEndpoints ? (
        <EdrDataCard toolbar={infectedEndpointsToolbar}>
          <Table
            columns={infectedEndpointColumns}
            data={infectedEndpoints as unknown as TableData[]}
            loading={loading}
            showPagination={false}
            onRowClick={(row) => {
              const item = row as unknown as WazuhMalwareEndpointItem;
              const systemWazuhAgentId = item.systemWazuhAgentId?.trim();
              if (!systemWazuhAgentId) return;
              navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
                state: { endpointName: item.endpoint ?? item.name },
              });
            }}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
        ) : null}

        {canList ? (
        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(v) => {
                setSearchInput(v);
                setPage(0);
              }}
              searchPlaceholder="Search Threats…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            onRowClick={(row) => openMalwareDetail(row as unknown as WazuhMalwareDetectionItem)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
        ) : null}
      </Box>
    </>
  );
};

export default MalwareDetection;
